import './style.css';
import {
  kardashevScale,
  kardashevTypeLabel,
  projectedPower,
  yearsToReachPower,
  KARDASHEV_THRESHOLDS_W,
  CURRENT_CIVILIZATION_POWER_W,
} from './engines/civilization';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function syncSliderFill(input: HTMLInputElement) {
  const min = parseFloat(input.min || '0');
  const max = parseFloat(input.max || '100');
  const val = parseFloat(input.value);
  const percent = ((val - min) / (max - min)) * 100;
  input.style.setProperty('--range-progress', `${percent}%`);
}

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
  updatePower();
  updateGrowthAndYears();
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator:onboarding-seen-civilization';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;
onboardingDismiss.addEventListener('click', () => {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* noop */
  }
});
helpBtn.addEventListener('click', () => onboarding.classList.remove('hidden'));
try {
  if (localStorage.getItem(ONBOARDING_KEY) === '1') onboarding.classList.add('hidden');
} catch {
  /* noop */
}

// ============================================================
// 左パネル: 現在のカルダシェフ値
// ============================================================
const powerSlider = document.getElementById('powerSlider') as HTMLInputElement;
const powerVal = document.getElementById('powerVal') as HTMLElement;
const kValueEl = document.getElementById('kValue') as HTMLElement;
const kTypeEl = document.getElementById('kType') as HTMLElement;

const POWER_MIN_EXP = 6;
const POWER_MAX_EXP = 40;
function sliderToPower(v: number): number {
  return Math.pow(10, POWER_MIN_EXP + (v / 1000) * (POWER_MAX_EXP - POWER_MIN_EXP));
}
function powerToSlider(p: number): number {
  return ((Math.log10(p) - POWER_MIN_EXP) / (POWER_MAX_EXP - POWER_MIN_EXP)) * 1000;
}

const POWER_PRESETS: Record<string, number> = {
  human: CURRENT_CIVILIZATION_POWER_W,
  typeI: KARDASHEV_THRESHOLDS_W.typeI,
  typeII: KARDASHEV_THRESHOLDS_W.typeII,
};

document.querySelectorAll<HTMLButtonElement>('.civ-panel:first-child .preset-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in POWER_PRESETS)) return;
    powerSlider.value = String(powerToSlider(POWER_PRESETS[key]));
    document.querySelectorAll('.civ-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    updatePower();
  });
});

function updatePower() {
  const power = sliderToPower(parseFloat(powerSlider.value));
  syncSliderFill(powerSlider);
  powerVal.textContent = power.toExponential(2);
  const k = kardashevScale(power);
  kValueEl.textContent = k.toFixed(2);
  const label = kardashevTypeLabel(k);
  kTypeEl.textContent = currentLang === 'ja' ? label.ja : label.en;
}

powerSlider.addEventListener('input', () => {
  document.querySelectorAll('.civ-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
  updatePower();
});

// ============================================================
// 右パネル: 成長シナリオ
// ============================================================
const growthSlider = document.getElementById('growthSlider') as HTMLInputElement;
const growthVal = document.getElementById('growthVal') as HTMLElement;
const yearsValue = document.getElementById('yearsValue') as HTMLElement;
const yearsLabel = document.getElementById('yearsLabel') as HTMLElement;

function sliderToGrowth(v: number): number {
  return v / 10000; // 例: 200 -> 0.02 (2%)
}
function growthToSlider(r: number): number {
  return r * 10000;
}

const GROWTH_PRESETS: Record<string, number> = {
  conservative: 0.005,
  trend: 0.02,
  optimistic: 0.05,
};

document.querySelectorAll<HTMLButtonElement>('.civ-panel:nth-child(2) .preset-btn:not(.target-btn)').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in GROWTH_PRESETS)) return;
    growthSlider.value = String(growthToSlider(GROWTH_PRESETS[key]));
    document.querySelectorAll('.civ-panel:nth-child(2) .preset-btn:not(.target-btn)').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    updateGrowthAndYears();
  });
});

let currentTarget: keyof typeof KARDASHEV_THRESHOLDS_W = 'typeI';
const TARGET_LABEL: Record<string, { ja: string; en: string }> = {
  typeI: { ja: 'タイプI', en: 'Type I' },
  typeII: { ja: 'タイプII', en: 'Type II' },
  typeIII: { ja: 'タイプIII', en: 'Type III' },
};

document.querySelectorAll<HTMLButtonElement>('.target-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const target = btn.getAttribute('data-target') as keyof typeof KARDASHEV_THRESHOLDS_W;
    if (!target) return;
    currentTarget = target;
    document.querySelectorAll('.target-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    updateGrowthAndYears();
  });
});

function formatYears(years: number): string {
  if (!isFinite(years)) return '∞';
  if (years >= 1e6) return years.toExponential(2);
  return Math.round(years).toLocaleString();
}

function updateGrowthAndYears() {
  const rate = sliderToGrowth(parseFloat(growthSlider.value));
  syncSliderFill(growthSlider);
  growthVal.textContent = `${(rate * 100).toFixed(2)}%`;

  const targetPower = KARDASHEV_THRESHOLDS_W[currentTarget];
  const years = yearsToReachPower(CURRENT_CIVILIZATION_POWER_W, targetPower, rate);
  yearsValue.textContent = formatYears(years);
  const targetLabel = TARGET_LABEL[currentTarget];
  const labelJa = `年後に${targetLabel.ja}へ`;
  const labelEn = `years to ${targetLabel.en}`;
  yearsLabel.setAttribute('data-ja', labelJa);
  yearsLabel.setAttribute('data-en', labelEn);
  yearsLabel.textContent = currentLang === 'ja' ? labelJa : labelEn;

  renderChart(rate, years, targetPower);
}

[growthSlider].forEach((el) => {
  el.addEventListener('input', () => {
    document.querySelectorAll('.civ-panel:nth-child(2) .preset-btn:not(.target-btn)').forEach((b) => b.classList.remove('active'));
    updateGrowthAndYears();
  });
});

// ============================================================
// 成長曲線チャート(対数スケール、SVG)
// ============================================================
const civChartSvg = document.getElementById('civChartSvg') as unknown as SVGSVGElement;
const civChartCaption = document.getElementById('civChartCaption') as HTMLElement;

function renderChart(rate: number, yearsToTarget: number, targetPower: number) {
  const current = CURRENT_CIVILIZATION_POWER_W;
  const maxYears = isFinite(yearsToTarget) && yearsToTarget > 0 ? yearsToTarget * 1.15 : 1000;
  const yMinLog = Math.log10(current) - 0.3;
  const yMaxLog = Math.log10(targetPower) + 0.3;

  const W = 1000;
  const H = 300;
  const numPoints = 120;

  function toXY(t: number): [number, number] {
    const p = projectedPower(current, rate, t);
    const logP = Math.log10(p);
    const x = (t / maxYears) * W;
    const yFrac = (logP - yMinLog) / (yMaxLog - yMinLog);
    const y = H - Math.max(0, Math.min(1, yFrac)) * H;
    return [x, y];
  }

  let pathD = '';
  for (let i = 0; i <= numPoints; i++) {
    const t = (i / numPoints) * maxYears;
    const [x, y] = toXY(t);
    pathD += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
  }

  const thresholds: { key: string; power: number; label: string }[] = [
    { key: 'I', power: KARDASHEV_THRESHOLDS_W.typeI, label: 'Type I' },
    { key: 'II', power: KARDASHEV_THRESHOLDS_W.typeII, label: 'Type II' },
    { key: 'III', power: KARDASHEV_THRESHOLDS_W.typeIII, label: 'Type III' },
  ];

  let linesHtml = '';
  thresholds.forEach((th) => {
    const logP = Math.log10(th.power);
    if (logP >= yMinLog && logP <= yMaxLog) {
      const yFrac = (logP - yMinLog) / (yMaxLog - yMinLog);
      const y = H - yFrac * H;
      linesHtml += `<line x1="0" y1="${y.toFixed(1)}" x2="${W}" y2="${y.toFixed(1)}" stroke="var(--magenta)" stroke-opacity="0.35" stroke-dasharray="6,5" />`;
      linesHtml += `<text x="${W - 8}" y="${(y - 6).toFixed(1)}" fill="var(--magenta)" font-size="13" font-family="IBM Plex Mono, monospace" text-anchor="end">${th.label}</text>`;
    }
  });

  civChartSvg.innerHTML = `
    ${linesHtml}
    <path d="${pathD}" fill="none" stroke="var(--magenta)" stroke-width="2.5" />
    <circle cx="0" cy="${(H - ((Math.log10(current) - yMinLog) / (yMaxLog - yMinLog)) * H).toFixed(1)}" r="5" fill="var(--amber)" />
  `;

  const nowLabel = currentLang === 'ja' ? '現在' : 'Now';
  const yearsLabelText = isFinite(yearsToTarget)
    ? currentLang === 'ja'
      ? `${formatYears(yearsToTarget)}年後に到達`
      : `Reached in ${formatYears(yearsToTarget)} years`
    : currentLang === 'ja'
      ? '成長率0%では到達しません'
      : 'Never reached at 0% growth';
  civChartCaption.textContent = `${nowLabel}: ${current.toExponential(2)} W  →  ${yearsLabelText}`;
}

// ============================================================
// 初期化
// ============================================================
powerSlider.value = String(powerToSlider(CURRENT_CIVILIZATION_POWER_W));
growthSlider.value = String(growthToSlider(0.02));
updatePower();
updateGrowthAndYears();

// ページローダーを隠す
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
